import { useState } from "react";
import {
  useDeleteAdMutation,
  useFetchAdsQuery,
  useToggleAdIsActiveStatusMutation,
} from "../store";
import Pagination from "../components/common/Pagination.tsx";
import ToggleSwitch from "../components/common/ToggleSwitch.tsx";
import NavigationLinkBtn from "../components/common/NavigationLinkBtn.tsx";
import { FaEdit, FaRegTrashAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import { Ad } from "../types";
import { showToast, ToastType } from "../utils/toast.utils.ts";
import Modal from "../components/common/Modal.tsx";
import Button from "../components/common/Button.tsx";
import { tdClasses, thClasses } from "../configs";

const Ads: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [adToDelete, setAdToDelete] = useState<Ad | null>(null);

  const [deleteAd] = useDeleteAdMutation();
  const [toggleActive] = useToggleAdIsActiveStatusMutation();

  const confirmDelete = (adToDelete: Ad) => {
    setAdToDelete(adToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteAd = async () => {
    if (!adToDelete) return;
    const result = await deleteAd(adToDelete._id);
    if (!result.error) {
      showToast("Bannière supprimée", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setAdToDelete(null);
  };

  const { data: ads } = useFetchAdsQuery({
    page,
    perPage,
  });

  return (
    <div className="w-full flex flex-col mx-auto p-4">
      <div className="flex items-center justify-between">
        <NavigationLinkBtn
          to="/ads/create"
          label="Créer une bannière"
        />
      </div>
      <table
        className="table-fixed w-full rounded-xl"
        style={{ borderRadius: "50px" }}
      >
        <colgroup>
          <col className="w-2/12" />
          <col className="w-2/12" />
          <col className="w-2/12" />
          <col className="w-2/12" />
          <col className="w-2/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
        </colgroup>
        <thead>
          <tr>
            <th
              key={0}
              className={thClasses}
            >
              Image
            </th>
            <th
              key={1}
              className={thClasses}
            >
              Villes
            </th>
            <th
              key={2}
              className={thClasses}
            >
              Active
            </th>
            <th
              key={3}
              className={thClasses}
            >
              Date de création
            </th>
            <th
              key={4}
              className={thClasses}
            >
              Mise à jour
            </th>
            <th
              key={5}
              className={thClasses}
            >
              Modifier
            </th>
            <th
              key={6}
              className={thClasses}
            >
              Supprimer
            </th>
          </tr>
        </thead>
        <tbody>
          {ads?.data?.map((row) => {
            return (
              <tr key={row._id}>
                <td className={tdClasses}>
                  <img
                    src={row.imagePath}
                    alt="bannière de publicité"
                  />
                </td>
                <td className={tdClasses}>
                  <div className="flex flex-col gap-2">
                    {row.cities.map((city) => (
                      <div key={city._id}> {city.displayName} </div>
                    ))}
                  </div>
                </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <ToggleSwitch
                      isOn={row.isActive}
                      onToggle={() => {
                        toggleActive({
                          adId: row._id,
                          isActive: !row.isActive,
                        });
                      }}
                    />
                  </div>
                </td>
                <td className={tdClasses}>{row.createdAt}</td>
                <td className={tdClasses}>{row.updatedAt}</td>
                <td className={tdClasses}>
                  <Link
                    className="w-full flex justify-center"
                    to={row._id}
                  >
                    <FaEdit className="w-8 text-primary" />{" "}
                  </Link>
                </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <FaRegTrashAlt
                      className="w-8 text-primary cursor-pointer"
                      onClick={() => {
                        confirmDelete(row);
                      }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        paginationInfo={{
          page,
          perPage,
          count: ads?.paginationInfo.count || 1,
        }}
        setPage={setPage}
        setPerPage={setPerPage}
      />
      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-300 px-4 py-2 rounded mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-primary text-white px-4 py-2 rounded"
                onClick={handleDeleteAd}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="bg-white text-center font-semibold">
            Êtes-vous sûr de vouloir supprimer la bannière ?
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Ads;
